(() => {
  const consentSelector = '[data-digital-content-consent]';
  const checkoutButtonSelector = '[data-digital-content-consent-checkout]';
  const consentVersion = 'v1';
  const legalBasis = 'OUG 34/2014 art. 16 lit. m';
  const consentNotePattern =
    /(?:^|\n{2})DIGITAL CONTENT CONSENT\nAccepted: Yes\nTimestamp: [^\n]+\nVersion: [^\n]+\nLegal basis: OUG 34\/2014 art\. 16 lit\. m(?=\n{2}|$)/g;
  const states = new WeakMap();

  const getState = (consent) => {
    if (!states.has(consent)) {
      states.set(consent, {
        queue: Promise.resolve(),
        revision: 0,
        timestamp: null,
      });
    }

    return states.get(consent);
  };

  // All consents that must be accepted before checkout (digital withdrawal waiver + policies).
  const getCheckboxes = (consent) => Array.from(consent.querySelectorAll('input[type="checkbox"][required]'));
  const allChecked = (consent) => {
    const boxes = getCheckboxes(consent);
    return boxes.length > 0 && boxes.every((box) => box.checked);
  };
  const getError = (consent) => consent.parentElement.querySelector('[data-digital-content-consent-error]');
  const getCheckoutButton = (consent) => {
    const formId = consent.dataset.consentFormId;
    return Array.from(document.querySelectorAll(checkoutButtonSelector)).find(
      (button) => button.getAttribute('form') === formId || button.form?.id === formId
    );
  };

  const setError = (consent, message = '') => {
    const error = getError(consent);
    getCheckboxes(consent).forEach((box) => box.toggleAttribute('aria-invalid', Boolean(message) && !box.checked));
    if (!error) return;

    if (message) error.textContent = message;
    error.hidden = !message;
  };

  const setCheckoutState = (consent, saving = false) => {
    const button = getCheckoutButton(consent);
    if (!button) return;

    button.disabled = !allChecked(consent) || saving;
    button.toggleAttribute('aria-disabled', button.disabled);
    button.toggleAttribute('aria-busy', saving);
  };

  const removeManagedConsentNote = (note = '') =>
    note
      .replace(/\r\n/g, '\n')
      .replace(consentNotePattern, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

  const consentOrderNote = (timestamp) =>
    [
      'DIGITAL CONTENT CONSENT',
      'Accepted: Yes',
      `Timestamp: ${timestamp}`,
      `Version: ${consentVersion}`,
      `Legal basis: ${legalBasis}`,
    ].join('\n');

  const buildCartNote = (existingNote, timestamp) => {
    const customerNote = removeManagedConsentNote(existingNote);
    if (!timestamp) return customerNote;
    return customerNote ? `${customerNote}\n\n${consentOrderNote(timestamp)}` : consentOrderNote(timestamp);
  };

  const getCurrentCartNote = async () => {
    const cartUrl = `${(window.routes?.cart_url || '/cart').replace(/\/$/, '')}.js`;
    const response = await fetch(cartUrl, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error(`Unable to read cart note (${response.status})`);
    const cart = await response.json();
    return cart.note || '';
  };

  const updateCartConsent = async (attributes, timestamp = null) => {
    const existingNote = await getCurrentCartNote();
    const response = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        attributes,
        note: buildCartNote(existingNote, timestamp),
      }),
    });

    if (!response.ok) throw new Error(`Unable to save digital consent (${response.status})`);
  };

  // These values are stored as evidence that the customer expressly requested immediate delivery of
  // digital content, acknowledged loss of the withdrawal right, and accepted the store policies.
  const acceptedAttributes = (consent, timestamp) => ({
    digital_content_consent: 'accepted',
    digital_content_consent_text: consent.dataset.consentText,
    digital_content_consent_timestamp: timestamp,
    digital_content_consent_version: consentVersion,
    digital_content_consent_legal_basis: legalBasis,
    fgo_invoice_notice: 'customer data may be used for invoicing and legal accounting records via FGO',
    digital_content_consent_source: 'cart',
    digital_content_consent_theme_version: consent.dataset.consentThemeVersion,
    digital_content_consent_store_domain: consent.dataset.consentStoreDomain,
    digital_content_consent_checkout_started_at: timestamp,
    policies_consent: 'accepted',
    policies_consent_text: consent.dataset.policiesConsentText || '',
    policies_consent_timestamp: timestamp,
  });

  const clearedAttributes = {
    digital_content_consent: '',
    digital_content_consent_text: '',
    digital_content_consent_timestamp: '',
    digital_content_consent_version: '',
    digital_content_consent_legal_basis: '',
    fgo_invoice_notice: '',
    digital_content_consent_source: '',
    digital_content_consent_theme_version: '',
    digital_content_consent_store_domain: '',
    digital_content_consent_checkout_started_at: '',
    policies_consent: '',
    policies_consent_text: '',
    policies_consent_timestamp: '',
  };

  const enqueueAttributeUpdate = (consent, attributes, timestamp = null) => {
    const state = getState(consent);
    state.queue = state.queue.catch(() => undefined).then(() => updateCartConsent(attributes, timestamp));
    return state.queue;
  };

  const ensureConsentTimestamp = (state) => {
    state.timestamp ||= new Date().toISOString();
    return state.timestamp;
  };

  const logConsentInDevelopment = (timestamp) => {
    const isDevelopment =
      window.Shopify?.designMode === true || ['localhost', '127.0.0.1'].includes(window.location.hostname);
    if (!isDevelopment) return;

    console.info(`[Digital Consent]\naccepted=true\ntimestamp=${timestamp}\nversion=${consentVersion}`);
  };

  const saveAcceptedConsent = async (consent) => {
    const state = getState(consent);
    if (!allChecked(consent)) throw new Error('Consent is not complete');

    const timestamp = ensureConsentTimestamp(state);
    await enqueueAttributeUpdate(consent, acceptedAttributes(consent, timestamp), timestamp);
  };

  const handleConsentChange = async (consent) => {
    const state = getState(consent);
    const revision = ++state.revision;
    setError(consent);

    if (!allChecked(consent)) {
      state.timestamp = null;
      setCheckoutState(consent);
      enqueueAttributeUpdate(consent, clearedAttributes).catch(() => undefined);
      return;
    }

    const timestamp = ensureConsentTimestamp(state);
    setCheckoutState(consent, true);

    try {
      await enqueueAttributeUpdate(consent, acceptedAttributes(consent, timestamp), timestamp);
      if (state.revision === revision && allChecked(consent)) {
        logConsentInDevelopment(timestamp);
        setCheckoutState(consent);
      }
    } catch (error) {
      console.error(error);
      if (state.revision !== revision || !allChecked(consent)) return;
      setError(consent, 'Consimțământul nu a putut fi salvat. Te rugăm să încerci din nou.');
      setCheckoutState(consent);
    }
  };

  const handleCheckout = async (event, form, button, consent) => {
    if (form.dataset.digitalConsentSubmitting === 'true') return;

    event.preventDefault();
    if (!allChecked(consent)) {
      setError(consent, 'Te rugăm să bifezi confirmările necesare înainte de finalizarea comenzii.');
      const firstUnchecked = getCheckboxes(consent).find((box) => !box.checked);
      firstUnchecked?.focus();
      setCheckoutState(consent);
      return;
    }

    setError(consent);
    setCheckoutState(consent, true);

    try {
      // Save again and await Shopify before allowing its native checkout redirect.
      await saveAcceptedConsent(consent);
      form.dataset.digitalConsentSubmitting = 'true';
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      button.removeAttribute('aria-busy');
      form.requestSubmit(button);
    } catch (error) {
      console.error(error);
      setError(consent, 'Consimțământul nu a putut fi salvat. Te rugăm să încerci din nou.');
      setCheckoutState(consent);
    }
  };

  const initializeConsent = (consent, reset = false) => {
    if (reset) {
      getCheckboxes(consent).forEach((box) => {
        box.checked = false;
      });
      const state = getState(consent);
      state.timestamp = null;
      state.revision += 1;
    }

    setError(consent);
    setCheckoutState(consent);
  };

  document.addEventListener('change', (event) => {
    const consent = event.target.closest?.(consentSelector);
    if (consent && event.target.matches('input[type="checkbox"]')) handleConsentChange(consent);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const button = event.submitter || document.activeElement;
    if (!button?.matches?.(checkoutButtonSelector)) return;

    const consent = Array.from(document.querySelectorAll(consentSelector)).find(
      (candidate) => candidate.dataset.consentFormId === form.id
    );
    if (consent) handleCheckout(event, form, button, consent);
  });

  const initializeAll = (reset = false) => {
    document.querySelectorAll(consentSelector).forEach((consent) => initializeConsent(consent, reset));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(true), { once: true });
  } else {
    initializeAll(true);
  }

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) initializeAll(true);
  });
})();
